import { cva, type VariantProps } from "class-variance-authority";
import * as React from "react";
import { cn } from "@volca/ui/lib/utils";

/**
 * A record's history, newest last: one line per event (an icon, who, what,
 * when), with a body only where someone wrote something.
 *
 * ```tsx
 * <ActivityFeed aria-label="Activity">
 *   <ActivityFeedItem>
 *     <ActivityFeedIcon><FlagIcon /></ActivityFeedIcon>
 *     <ActivityFeedLine>
 *       <ActivityFeedActor>Weekly GM tracker</ActivityFeedActor> flagged this job
 *       <ActivityFeedTime dateTime="2026-10-19T09:00:00Z">2h ago</ActivityFeedTime>
 *     </ActivityFeedLine>
 *     <ActivityFeedBody>Equipment invoiced 2×, sold 1×</ActivityFeedBody>
 *   </ActivityFeedItem>
 * </ActivityFeed>
 * ```
 */
export const ActivityFeed = ({
  className,
  ...props
}: React.ComponentProps<"ol">) => (
  <ol
    data-slot="activity-feed"
    className={cn("flex w-full flex-col gap-1", className)}
    {...props}
  />
);

export const ActivityFeedItem = ({
  className,
  ...props
}: React.ComponentProps<"li">) => (
  <li
    data-slot="activity-feed-item"
    className={cn(
      "grid grid-cols-[1.25rem_minmax(0,1fr)] items-start gap-x-2 py-1",
      className,
    )}
    {...props}
  />
);

const iconVariants = cva(
  "flex size-5 items-center justify-center [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      tone: {
        neutral: "text-subtle",
        ai: "text-accent-purple",
        warning: "text-warning",
        success: "text-success",
        danger: "text-danger",
      },
    },
    defaultVariants: { tone: "neutral" },
  },
);

export const ActivityFeedIcon = ({
  tone,
  className,
  ...props
}: React.ComponentProps<"span"> & VariantProps<typeof iconVariants>) => (
  <span
    aria-hidden
    data-slot="activity-feed-icon"
    className={cn(iconVariants({ tone }), className)}
    {...props}
  />
);

/** Who did what, with when at the end of the line. */
export const ActivityFeedLine = ({
  className,
  ...props
}: React.ComponentProps<"p">) => (
  <p
    data-slot="activity-feed-line"
    className={cn(
      "flex min-w-0 flex-wrap items-baseline gap-x-1 text-sm leading-5 text-subtle",
      className,
    )}
    {...props}
  />
);

export const ActivityFeedActor = ({
  className,
  ...props
}: React.ComponentProps<"span">) => (
  <span
    data-slot="activity-feed-actor"
    className={cn("font-medium text-ink", className)}
    {...props}
  />
);

/** Pass the instant as `dateTime`; the children are how it reads. */
export const ActivityFeedTime = ({
  className,
  children,
  ...props
}: React.ComponentProps<"time">) => (
  <time
    data-slot="activity-feed-time"
    className={cn("ml-auto pl-3 whitespace-nowrap tabular-nums", className)}
    {...props}
  >
    {children}
  </time>
);

/** What someone wrote, under the line it belongs to. */
export const ActivityFeedBody = ({
  className,
  ...props
}: React.ComponentProps<"div">) => (
  <div
    data-slot="activity-feed-body"
    className={cn(
      "col-start-2 mt-1 border-l-2 border-line pl-3 text-sm leading-5 text-ink",
      className,
    )}
    {...props}
  />
);

/** A heading over a stretch of the feed: "This week", "October". */
export const ActivityFeedGroup = ({
  className,
  ...props
}: React.ComponentProps<"h3">) => (
  <h3
    data-slot="activity-feed-group"
    className={cn(
      "pt-3 pb-1 text-sm font-medium text-ink first:pt-0",
      className,
    )}
    {...props}
  />
);

/**
 * The thing an event was about, set under its line as a card: a flag, a
 * meeting, a document. The bar on its edge takes the tone of the event.
 */
export const ActivityFeedCard = ({
  tone = "neutral",
  className,
  ...props
}: React.ComponentProps<"div"> & {
  tone?: "neutral" | "warning" | "success";
}) => (
  <div
    data-slot="activity-feed-card"
    className={cn(
      "col-start-2 mt-1.5 flex min-w-0 flex-col gap-0.5 rounded-lg border border-line bg-surface-raised py-2 pr-3 pl-3 text-sm",
      "relative before:absolute before:inset-y-2 before:left-0 before:w-0.5 before:rounded-full",
      {
        "before:bg-warning": tone === "warning",
        "before:bg-success": tone === "success",
        "before:bg-line-strong": tone === "neutral",
      },
      className,
    )}
    {...props}
  />
);
